import { useContext, useState } from 'react'
import { AvailabilitySelector, CustomModal } from '@components'
import { availabilityTypes } from "@config"
import { NotificationContext } from "@context"
import { UPDATE_AVAILABILITY_MUTATION } from "@graphql/mutations"
import { calculateSlots, getGraphQLErrorMessage, mySqlToLocal } from "@helpers"
import { useMutation } from "@apollo/client"

import { 
  Button, 
  ModalBody, 
  ModalContent, 
  ModalFooter, 
  ModalHeader 
} from '@heroui/react'

import _orderBy from "lodash/orderBy"

import PropTypes from 'prop-types'

const BulkAvailabilityModal = ({ handleClose, handleRefresh, groups, session }) => {
  const [availabilityType, setAvailabilityType] = useState(availabilityTypes.AVAILABLE)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedStartTime, setSelectedStartTime] = useState(null)
  const [selectedEndTime, setSelectedEndTime] = useState(null)
  const [selectedGroupIds, setSelectedGroupIds] = useState([])

  const { notify } = useContext(NotificationContext)
  
  const [updateAvailability, {loading: isSaving}] = useMutation(UPDATE_AVAILABILITY_MUTATION, {
    onCompleted: (data) => {
      notify({ title: "Availability Updated", type: "success", content: `${data.updateAvailability.no_of_slots_updated} slots updated to ${availabilityType.label}, ${data.updateAvailability.no_of_slots_skipped} slots skipped. Slots can be skipped if they are already booked or already have the desired status.`, open: true })
      handleRefresh()
      handleClose()
    },

    onError: (error) => {
      notify({ title: "Error Updating Availability", type: "danger", content: getGraphQLErrorMessage(error, "There was a problem updating availability. Please try again."), open: true })
    }
  })

  const handleAvailabilityTypeChange = (selectedOptionId) => {
    const selectedOption = Object.values(availabilityTypes).find(
      option => option.id === selectedOptionId
    )

    setAvailabilityType(selectedOption)
  }

  const handleClearAll = () => setSelectedGroupIds([])

  const handleEndTimeChange = (time) => setSelectedEndTime(time)
  
  const handleSave = (slots) => {
    const noOfSlotsAffected = selectedStartTime && selectedEndTime 
      ? slots.filter((slot) => slot.starts_at.format("HH:mm") >= selectedStartTime && slot.starts_at.format("HH:mm") <= selectedEndTime).length
      : 0

    updateAvailability({
      variables: {
        input: {
          starts_at: mySqlToLocal(session.starts_at).format("YYYY-MM-DD") + " " + selectedStartTime + ":00",
          ends_at: mySqlToLocal(session.ends_at).format("YYYY-MM-DD") + " " + selectedEndTime + ":00",
          session_id: session.id,
          status: availabilityType.code,
          group_ids: selectedGroupIds,
          no_of_slots: noOfSlotsAffected,
        }
      }
    })
  }

  const handleSelectAll = () => setSelectedGroupIds(groups.map(group => group.id))
  const handleSearchChange = (searchTerm) => setSearchTerm(searchTerm)
  const handleSearchClear = () => setSearchTerm("")
  
  const handleStartTimeChange = (time) => {
    setSelectedStartTime(time)

    if (selectedEndTime && selectedEndTime < time) {
      setSelectedEndTime(null)
    }
  }

  const handleToggleSelected = (groupId) => {
    (selectedGroupIds.includes(groupId))
      ? setSelectedGroupIds(selectedGroupIds.filter(id => id !== groupId))
      : setSelectedGroupIds([...selectedGroupIds, groupId])
  }

  const noOfSelected = selectedGroupIds.length
  const noOfDeselected = groups.length - noOfSelected
  const filteredOrderedGroups = _orderBy(groups.filter(group => group.title.toLowerCase().includes(searchTerm.toLowerCase())), ["title"], ["asc"])
  const slots = session ? calculateSlots(session) : []
  const isSaveDisabled = !selectedStartTime || !selectedEndTime || selectedGroupIds.length === 0
  
  return (
    <CustomModal
      isOpen={true}
      size="2xl"
      handleClose={() => handleClose()}
      scrollBehavior="inside"
      isDismissable={false}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">Bulk Availability Update</ModalHeader>

            <ModalBody>
              <AvailabilitySelector
                availabilityType={availabilityType}
                availabilityTypes={availabilityTypes}
                endTime={selectedEndTime}
                groups={filteredOrderedGroups}
                handleClearAll={handleClearAll}
                handleSearchChange={handleSearchChange}
                handleSearchClear={handleSearchClear}
                handleSelectAll={handleSelectAll}
                handleToggleSelected={handleToggleSelected}
                noOfDeselected={noOfDeselected}
                noOfSelected={noOfSelected}
                searchTerm={searchTerm}
                selectedGroupIds={selectedGroupIds}
                slots={slots}
                startTime={selectedStartTime}
                handleStartTimeChange={handleStartTimeChange}
                handleEndTimeChange={handleEndTimeChange}
                handleAvailabilityTypeChange={handleAvailabilityTypeChange}
              />
            </ModalBody>
            
            <ModalFooter>
              <div className="flex justify-between w-full">
                <Button onPress={() => handleClose()} tabIndex={-1}>Cancel</Button>
                <Button color="secondary" onPress={() => handleSave(slots)} isLoading={isSaving} isDisabled={isSaveDisabled}>Apply Changes</Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </CustomModal>
  )
}

BulkAvailabilityModal.propTypes = {
  groups: PropTypes.array.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func.isRequired,
  session: PropTypes.object.isRequired,
}

export default BulkAvailabilityModal